import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { EtapasEdicaoTable } from "components/EtapaEdicaoList";
import { UpdateEditionForm } from "components/UpdateEditionForm";
import { useLocation } from "react-router-dom";
import {
  Edicao,
  Etapa,
  UpdateEtapasEdicao,
  useGetEdicaoByIdQuery,
  useUpdateEdicaoMutation,
  useUpdateEtapasEdicaoMutation,
  useUpdateInscricoesAbertaMutation,
} from "shared/features/api/edicao/edicaoSlice";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateEdition } from "shared/schemas/updateEdition";
import { IUpdateEditionForm } from "shared/interfaces";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

export const UpdateEdition: React.FC = () => {
  const { state } = useLocation();
  const {
    data: edicao,
    isLoading: carregandoEdicao,
    isError: edicaoNaoEncontrada,
  } = useGetEdicaoByIdQuery(state.idEdicao);
  const [updateEdicao, { isLoading: carregandoUpdateEdicao }] =
    useUpdateEdicaoMutation();
  const [updateAbertura] = useUpdateInscricoesAbertaMutation();
  const [updateEtapasEdicao] = useUpdateEtapasEdicaoMutation();
  const [etapasData, setEtapasData] = useState<Etapa[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [etapasMudaram, setEtapasMudaram] = useState<boolean>(false);

  useEffect(() => {
    if (edicao) {
      setEtapasData([...edicao.etapas].sort((a, b) => a.ordem - b.ordem));
    }
  }, [edicao]);

  useEffect(() => {
    if (edicao?.etapas) {
      const mudaram = !arraysIguais(
        [...edicao.etapas].sort((a, b) => a.ordem - b.ordem),
        etapasData
      );
      setEtapasMudaram(mudaram);
    }
  }, [etapasData, edicao]);

  function datasSaoIguais(d1?: Date | null, d2?: Date | null): boolean {
    if (!d1 && !d2) return true;
    if (!d1 || !d2) return false;
    return d1.getTime() === d2.getTime();
  }


  const formMethods = useForm<
    IUpdateEditionForm & {
      originalDataAbertura?: string | null;
      originalDataFim?: string | null;
    }
  >({
    resolver: yupResolver(updateEdition),
    defaultValues: useMemo(() => {
      if (!edicao) return undefined;

      return {
        nome: edicao.nome,
        notaCorte: edicao.notaCorte,
        inscricoesAberta: edicao.inscricoesAberta,
        dataAberturaInscricao: edicao.dataAberturaInscricao,
        dataFimInscricao: edicao.dataFimInscricao,
        originalDataAbertura: edicao.dataAberturaInscricao,
        originalDataFim: edicao.dataFimInscricao,
        aberturaAgendada: edicao.aberturaAgendada,
      };
    }, [edicao]),
    mode: "onChange",
  });

  useEffect(() => {
    if (edicao && formMethods) {
      formMethods.reset({
        nome: edicao.nome,
        notaCorte: edicao.notaCorte,
        inscricoesAberta: edicao.inscricoesAberta,
        dataAberturaInscricao: edicao.dataAberturaInscricao,
        dataFimInscricao: edicao.dataFimInscricao,
        originalDataAbertura: edicao.dataAberturaInscricao,
        originalDataFim: edicao.dataFimInscricao,
        aberturaAgendada: edicao.aberturaAgendada,
      });
    }
  }, [edicao, formMethods]);

  function arraysIguais(a: Etapa[], b: Etapa[]): boolean {
    return (
      a.length === b.length &&
      a.every(
        (etapa, index) =>
          etapa.idEtapa === b[index].idEtapa && etapa.ordem === b[index].ordem && etapa.peso === b[index].peso
      )
    );
  }
  
  const handleSubmitEdition: SubmitHandler<IUpdateEditionForm> = async (
    formData
  ) => {
    if (!edicao) return;

    let aberturaEdicao;
    let aberturaForm;
    if (edicao.dataAberturaInscricao !== "") {
      aberturaEdicao = new Date(edicao.dataAberturaInscricao);
      aberturaForm = formData.dataAberturaInscricao
        ? new Date(formData.dataAberturaInscricao)
        : null;
    }

    let fechamentoEdicao;
    let fechamentoForm;
    if (edicao.dataFimInscricao !== "") {
      fechamentoEdicao = new Date(edicao.dataFimInscricao);
      fechamentoForm = formData.dataFimInscricao
        ? new Date(formData.dataFimInscricao)
        : null;
    }
    
    const dadosEdicaoMudaram =
      formData.nome !== edicao.nome ||
      formData.notaCorte !== edicao.notaCorte ||
      !datasSaoIguais(aberturaForm, aberturaEdicao) ||
      formData.aberturaAgendada !== edicao.aberturaAgendada ||
      !datasSaoIguais(fechamentoForm, fechamentoEdicao);



    const statusInscricoesMudou =
      formData.inscricoesAberta !== edicao.inscricoesAberta;

    try {
      if (etapasMudaram) {
        const payloadEtapasEdicao = {
          idEdicao: edicao.idEdicao,
          etapas: etapasData as UpdateEtapasEdicao[]
        };

        await toast.promise(updateEtapasEdicao(payloadEtapasEdicao).unwrap(), {
          pending: "Carregando...",
          success: `Etapas da edição alteradas com sucesso!`,
          error: {
            render({ data }) {
              const response = data as {
                data?: {
                  errors?: string[];
                  message?: string;
                };
              };

              const errorMessage =
                response.data?.errors?.[0] ||
                response.data?.message ||
                `Erro ao  alterar etapas da edição.`;
              return errorMessage;
            },
          },
        });
      }

      if (statusInscricoesMudou) {
        const payloadAberturaInscricoes = {
          idEdicao: edicao.idEdicao,
          abrir: formData.inscricoesAberta === "T" ? true : false,
        };

        await toast.promise(
          updateAbertura(payloadAberturaInscricoes).unwrap(),
          {
            pending: "Carregando...",
            success: `Inscrições  ${
              formData.inscricoesAberta === "T" ? "abertas" : "fechadas"
            } com sucesso!`,
            error: {
              render({ data }) {
                const response = data as {
                  data?: {
                    errors?: string[];
                    message?: string;
                  };
                };

                const errorMessage =
                  response.data?.errors?.[0] ||
                  response.data?.message ||
                  `Erro ao  ${
                    formData.inscricoesAberta === "T" ? "abrir" : "fechar"
                  } incrições da edição.`;
                return errorMessage;
              },
            },
          }
        );
      }

      if (dadosEdicaoMudaram) {
        const payloadEdicao = {
          idEdicao: edicao.idEdicao,
          edicao: {
            nome: formData.nome! === edicao.nome ? null : formData.nome!,
            notaCorte:
              formData.notaCorte! === edicao.notaCorte
                ? null
                : formData.notaCorte!,
            dataAberturaInscricao: formData.dataAberturaInscricao!,
            dataFimInscricao: formData.dataFimInscricao!,
            aberturaAgendada:
              formData.aberturaAgendada! === edicao.aberturaAgendada
                ? (
                    !datasSaoIguais(fechamentoEdicao, fechamentoForm) ||
                    !datasSaoIguais(aberturaEdicao, aberturaForm)
                  )
                  ? formData.aberturaAgendada!
                  : null
                : formData.aberturaAgendada!,
          },
        };



        await toast.promise(updateEdicao(payloadEdicao).unwrap(), {
          pending: "Carregando...",
          success: "Edição editada com sucesso!",
          error: {
            render({ data }) {
              const response = data as {
                data?: {
                  errors?: string[];
                  message?: string;
                };
              };

              const errorMessage =
                response.data?.errors?.[0] ||
                response.data?.message ||
                "Erro ao editar edição.";

              return errorMessage;
            },
          },
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  function valoresIguaisFormularioEEdicao(
    formData: IUpdateEditionForm,
    edicao: Edicao,
    etapas: Etapa[]
  ): boolean {
    const nomeIgual = formData.nome === edicao.nome;
    const notaCorteIgual = Number(formData.notaCorte ?? null) === Number(edicao.notaCorte ?? null);
    const aberturaAgendadaIgual = formData.aberturaAgendada === edicao.aberturaAgendada;
    const inscricoesAbertaIgual = formData.inscricoesAberta === edicao.inscricoesAberta;
    const dataAberturaIgual = formData.dataAberturaInscricao === edicao.dataAberturaInscricao;
    const dataFimIgual = formData.dataFimInscricao === edicao.dataFimInscricao;
    const etapasIguais = arraysIguais([...edicao.etapas].sort((a, b) => a.ordem - b.ordem), etapas);

    return (
      nomeIgual &&
      notaCorteIgual &&
      aberturaAgendadaIgual &&
      inscricoesAbertaIgual &&
      dataAberturaIgual &&
      dataFimIgual &&
      etapasIguais
    );
  }



  // Atualizar validade do formulário
  useEffect(() => {
    const subscription = formMethods.watch((formData) => {
      if (!edicao) return;

      const formValido = formMethods.formState.isValid;
      const camposEssenciaisPreenchidos =
        formData.nome !== undefined &&
        formData.notaCorte !== undefined &&
        formData.inscricoesAberta !== undefined &&
        formData.aberturaAgendada !== undefined;

      const nadaMudou =
        camposEssenciaisPreenchidos &&
        valoresIguaisFormularioEEdicao(
          formData as IUpdateEditionForm,
          edicao,
          etapasData
        );

      setIsFormValid(formValido && !nadaMudou);
    });

    return () => subscription.unsubscribe();
  }, [formMethods, edicao, etapasData]);


  return (
    <>
      {carregandoEdicao ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : edicaoNaoEncontrada ? (
        <Grid item xs={12}>
          <Typography
            variant="caption"
            fontSize={18}
            sx={{ textAlign: "center" }}
          >
            Edição não encontrada!
          </Typography>
        </Grid>
      ) : (
        <>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <Box
              component="form"
              onSubmit={formMethods.handleSubmit(handleSubmitEdition)}
              sx={{ p: 2, flexGrow: 1 }}
            >
              <Typography
                variant="caption"
                fontSize={18}
                sx={{ textAlign: "center" }}
              >
                {edicao?.nome}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6} lg={5}>
                  <Paper elevation={2} sx={{ height: "100%" }}>
                    <FormProvider {...formMethods}>
                      <UpdateEditionForm edicao={edicao!} />
                    </FormProvider>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6} lg={7}>
                  <Paper elevation={2} sx={{ height: "100%" }}>
                    <EtapasEdicaoTable
                      etapas={etapasData}
                      onEtapasChange={setEtapasData}
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={carregandoUpdateEdicao || (!etapasMudaram && !isFormValid)}
                >
                  {carregandoUpdateEdicao ? "Carregando..." : "Atualizar"}
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </>
      )}
    </>
  );
};
